// FAMILY-EXT: файл-пикер для внешнего WebDAV-хранилища (Synology Drive).
// Открывается из AttachmentAddStep. Принимает storage (запись из БД).
// Навигация по папкам кликами, выбор файла → onPick({externalPath, name}).

import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon, Loader } from 'semantic-ui-react';
import { Popup } from '../../lib/custom-ui';

import api from '../../api';
import { getAccessToken } from '../../utils/access-token-storage';

import styles from './WebdavBrowserStep.module.scss';

const splitPath = (p) =>
  String(p || '/')
    .split('/')
    .filter(Boolean);

const parentPath = (p) => {
  const parts = splitPath(p);
  if (parts.length <= 1) return '/';
  return `/${parts.slice(0, -1).join('/')}`;
};

const WebdavBrowserStep = React.memo(({ storage, onPick, onClose }) => {
  const [t] = useTranslation();
  const [currentPath, setCurrentPath] = useState('/');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (path) => {
      setLoading(true);
      setError(null);
      try {
        const token = getAccessToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const body = await api.webdavBrowse(storage.id, path, headers);
        const sorted = [...(body.items || [])].sort((a, b) => {
          if (a.type !== b.type) return a.type === 'directory' ? -1 : 1;
          return a.basename.localeCompare(b.basename);
        });
        setItems(sorted);
        setCurrentPath(body.path || path);
      } catch (e) {
        setError(e && e.message ? e.message : 'Ошибка загрузки');
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [storage],
  );

  useEffect(() => {
    load('/');
  }, [load]);

  const handleItemClick = useCallback(
    (item) => {
      if (item.type === 'directory') {
        load(item.filename);
      } else {
        onPick({ externalPath: item.filename, name: item.basename });
        onClose();
      }
    },
    [load, onPick, onClose],
  );

  const handleUpClick = useCallback(() => {
    load(parentPath(currentPath));
  }, [load, currentPath]);

  const parts = splitPath(currentPath);
  const canGoUp = parts.length > 0;

  return (
    <>
      <Popup.Header onBack={canGoUp ? handleUpClick : undefined}>
        {storage.name}
      </Popup.Header>
      <Popup.Content>
        <div className={styles.breadcrumb}>
          <Icon name="folder open" /> /{parts.join(' / ')}
        </div>
        {loading && (
          <div className={styles.center}>
            <Loader active inline size="small" />
          </div>
        )}
        {!loading && error && <div className={styles.error}>{error}</div>}
        {!loading && !error && items.length === 0 && (
          <div className={styles.empty}>{t('common.emptyFolder')}</div>
        )}
        {!loading && !error && items.length > 0 && (
          <ul className={styles.list}>
            {items.map((item) => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events,
              // jsx-a11y/no-noninteractive-element-interactions
              <li
                key={item.filename}
                className={styles.item}
                onClick={() => handleItemClick(item)}
              >
                <Icon
                  name={item.type === 'directory' ? 'folder' : 'file'}
                  className={styles.icon}
                />
                <span className={styles.name}>{item.basename}</span>
                {item.type === 'file' && item.size > 0 && (
                  <span className={styles.size}>
                    {(item.size / 1024).toFixed(1)} KB
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Popup.Content>
    </>
  );
});

WebdavBrowserStep.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  storage: PropTypes.object.isRequired,
  /* eslint-enable react/forbid-prop-types */
  onPick: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default WebdavBrowserStep;
